import React, { useEffect, useState } from 'react';
import './style.scss';

interface ProgressBarProps {
  label: string;
  value: number;
  tooltip?: string;
}

const thresholds = [
  { limit: 40, color: '#d3263e', label: 'Critical' },
  { limit: 65, color: '#f7850dff', label: 'Poor' },
  { limit: 80, color: '#f7a80bff', label: 'Fair' },
  { limit: 90, color: '#44c710c9', label: 'Good' },
  { limit: 100, color: '#02f52aff', label: 'Perfect' },
];

const ProgressBar: React.FC<ProgressBarProps> = ({ label, value, tooltip }) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  const getStateLabel = (val: number): string => {
    for (const threshold of thresholds) {
      if (val <= threshold.limit) return threshold.label;
    }
    return thresholds[thresholds.length - 1].label;
  };

  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const frameRate = 60;
    const totalFrames = Math.round((duration / 1000) * frameRate);
    const increment = value / totalFrames;
    let currentFrame = 0;

    const interval = setInterval(() => {
      currentFrame++;
      start += increment;
      if (currentFrame >= totalFrames) {
        setAnimatedValue(value);
        clearInterval(interval);
      } else {
        setAnimatedValue(parseFloat(start.toFixed(1)));
      }
    }, 1000 / frameRate);

    return () => clearInterval(interval);
  }, [value]);

  const generateGradient = () => {
    const stops: string[] = [];
    let prevLimit = 0;

    for (const { limit, color } of thresholds) {
      const startPercent = (prevLimit / 100) * animatedValue;
      const endPercent = (Math.min(limit, 100) / 100) * animatedValue;

      if (animatedValue >= prevLimit) {
        stops.push(`${color} ${startPercent}%`, `${color} ${endPercent}%`);
      }

      prevLimit = limit;

      if (animatedValue < limit) break;
    }

    return `linear-gradient(to right, ${stops.join(', ')})`;
  };

  return (
    <div className="pi-card">
      <span className="pi-label" title={tooltip}>
        {label}
      </span>
      <div
        className="pi-progress-container"
        title={getStateLabel(animatedValue)}
      >
        <div
          className="pi-progress-bar"
          style={{
            width: `${animatedValue}%`,
            background: generateGradient(),
          }}
        />
      </div>
      <span className="pi-value" title={getStateLabel(animatedValue)}>
        {Math.round(animatedValue)}%
      </span>
    </div>
  );
};

export default ProgressBar;
